import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useReducer } from 'react';
import { Stage, Layer, Rect, Group, Text, Circle, Line } from 'react-konva';
import { checkIfLetterIsValid, detectIfTextIsRTL, getLayout, getPromotionMainColor, getSoundPropsById, i18nIntl } from '../../lib/ep.js';
import { FormattedMessage } from 'react-intl';
import { Howl } from 'howler';
import { imgPath, _derange, isColorBright, RGB_2_HEX } from '../../lib/ep.js';
import InstructionsHand from '../svg/InstructionsHand.js';

import Spinner from '../builder/Spinner';
import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';
import { localizeFields } from '../builder/DynamicFields.js';

const standard_board_size = 800;	// px
const max_letters_x_line = 15;		// characters
const min_letters_x_word = 1;		// characters
const stroke_anim_duration = 0.55;
const stage_avail_w_prop = 0.94;	// %
const cell_padding = 2;				// px

const points_no_fail = 100;
const points_with_fail = 50;

const time_display_skip = 10;	//sec.

const HightlightedText = (props) => {
	const canvas_size = props?.canvas_size ?? standard_board_size;
	const reference = props?.reference ?? null;
	const avail_w = props?.avail_w ?? 1;
	const pos_y = props?.y ?? 0;
	const stroke_width = props?.stroke_width ?? 1.5;
	const stroke_opacity = props?.stroke_opacity ?? 1;
	const typography = props?.typography ?? 'Arial';
	const font_size = props?.font_size ?? 50;
	const text_color = props?.color ?? 'rgba(255, 255, 255, 1)';

	const is_text_color_bright = isColorBright(RGB_2_HEX(text_color));

	const _w = avail_w * canvas_size;
	const _h = 0.5 * canvas_size;
	const canvas_scale = Math.min(1, (1.3 * canvas_size / standard_board_size));

	const text_props = {
		width:_w,
		height:_h,
		fontSize:canvas_scale * font_size,
		fontFamily:typography,
		wrap:'word',
		align:'center',
		verticalAlign:'middle',
	};

	return (
		<Group
			ref={reference}
			offsetX={0.5 * _w}
			offsetY={0.5 * _h}
			x={0.5 * canvas_size}
			y={pos_y + 0.5 * _h}
		>
			<Text
				{...text_props}
				fill={(is_text_color_bright) ? 'black' : 'white'}
				stroke={(is_text_color_bright) ? 'black' : 'white'}
				strokeWidth={Math.min(3.5, Math.max(1.3, canvas_scale * stroke_width))}
				opacity={stroke_opacity}
				lineJoin='round'
				lineCap='round'
				text={props.children}
			/>

			<Text
				{...text_props}
				fill={text_color}
				text={props.children}
			/>
		</Group>
	)
};

const SkipButton = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const color_ok = props?.color_ok ?? 'rgba(120, 195, 0, 1)';
	const is_color_bright = isColorBright(RGB_2_HEX(color_ok));
	
	const icon_size = 12;
	const icon_scale = icon_size / 60;	// tick done within an icon 60x60
	const padding = 8;
	const stroke_width = 1.5;

	const v_anchor = props?.v_anchor ?? 'top';
	const h_anchor = props?.h_anchor ?? 'right';
	const container_size = props?.container_size ?? standard_board_size;
	const refs = useRef({});
	const [ button_width, setButtonWidth ] = useState(4 * icon_size + 2 * padding);

	const icon_props = {
		stroke:(is_color_bright) ? 'black' : 'white',
		strokeWidth:1.5 / icon_scale,
		lineCap:'round',
		lineJoin:'round',
	};

	const fadeIn = () => {
		if(refs.current[`skip_btn`] == undefined) return;

		refs.current[`skip_btn`].to({
			opacity:1,
			duration:stroke_anim_duration,
			easing:Konva.Easings.EaseInOut,
		});
	};

	const fadeOut = () => {
		if(refs.current[`skip_btn`] == undefined) return;

		refs.current[`skip_btn`].to({
			opacity:0,
			duration:stroke_anim_duration,
			easing:Konva.Easings.EaseInOut,
		});
	};

	useEffect(() => {
		if(refs.current['btn_label'] != undefined) {
			setButtonWidth(refs.current['btn_label'].getTextWidth() + 3 * padding + icon_size);
			refs.current[`skip_btn`].draw();

			setTimeout(() => {
				fadeIn();
			}, 50);
		}

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			fadeOut();
		}
	}, []);

	let main_x;
	let main_y;

	switch(v_anchor) {
		case 'top':
		default:
			main_y = 0.5 * stroke_width;
			break;

		case 'bottom':
			main_y = container_size - 0.5 * stroke_width - icon_size + 2 * padding;
			break;
	}

	switch(h_anchor) {
		case 'left':
			main_x = 0.5 * stroke_width;
			break;

		case 'right':
		default:
			main_x = container_size - 0.5 * stroke_width - button_width;
			break;
	}

	return (
		<Group
			ref={(node) => { refs.current[`skip_btn`] = node; }}
			opacity={0}
			x={main_x}
			y={main_y}
			onClick={props.onClick}
			onTap={props.onClick}
		>
			<Rect
				height={icon_size + 2 * padding}
				width={button_width}
				fill={color_ok}
				stroke={(is_color_bright) ? 'black' : 'white'}
				strokeWidth={stroke_width}
			/>

			<Group
				scaleX={icon_scale}
				scaleY={icon_scale}
				x={padding}
				y={padding}
			>
				<Line
					{...icon_props}
					points={[0, 0, 35, 30, 0, 60]}
				/>

				<Line
					{...icon_props}
					points={[25, 0, 60, 30, 25, 60]}
				/>
			</Group>

			<Text
				ref={(node) => { refs.current[`btn_label`] = node; }}
				height={icon_size}
				fontSize={12}
				lineHeight={1}
				fontFamily={props?.typography ?? 'Arial'}
				fill={(is_color_bright) ? 'black' : 'white'}
				verticalAlign='middle'
				text={i18n("Skip word", 'uppercase')}
				x={icon_size + 2 * padding}
				y={padding}
			/>
		</Group>
	)
};

const GuessWordCanvas = forwardRef((props, ref) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const stage = props?.stage ?? {};

	if(stage.guessword_words != undefined) {
		stage.guessword_words=localizeFields(stage.guessword_words,props.locale);
	}
	
	const promotion = props?.promotion ?? {};
	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	let is_playable = props?.is_playable ?? ((!is_builder || is_test) ? true : false);
	let is_skip_enabled = stage?.guessword_skip_time ?? false;
	const size = useRef(standard_board_size);
	const level = stage?.guessword_level ?? 'normal';
	const is_hard_mode = (level == 'hard') ? true : false;
	const appear_method = stage?.appear_method ?? 'left';	// 'left' || 'bottom'
	const letter_layout = stage?.guessword_font_bg ?? 'circle';	// 'circle' || 'square' || 'image'
	const display_outter_border = stage?.display_outter_border ?? true;

	const promo_main_color = getPromotionMainColor(promotion);

	const main_color = stage?.bg_color ?? promo_main_color;
	const is_main_color_bright = isColorBright(RGB_2_HEX(main_color));

	const text_color = stage?.guessword_font_color ?? promo_main_color;
	const background_color = stage?.guessword_bg_color ?? `rgba(${(is_main_color_bright) ? '0, 0, 0' : '255, 255, 255'}, 0.35)`;
	const is_bg_color_bright = isColorBright(RGB_2_HEX(background_color));
	
	const text_color_ok = stage?.guessword_font_color_ok ?? promo_main_color;
	const background_color_ok = stage?.guessword_bg_color_ok ?? ((is_main_color_bright) ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)');
	const hint_text_color = 'rgba(255, 255, 255, 1)';

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	/* SOUNDS - ini */
	const sound_perfect = new Howl({ ...getSoundPropsById('cross') });
	const soundPerfect = () => sound_perfect.play();

	const sound_success = new Howl({ ...getSoundPropsById('success') });
	const soundSuccessWord = () => sound_success.play();

	const sound_slide = new Howl({ ...getSoundPropsById('slide') });
	const soundSlide = () => sound_slide.play();

	const sound_error = new Howl({ ...getSoundPropsById('error_backflip') });
	const soundError = () => sound_error.play();

	const sound_end_ok = new Howl({ ...getSoundPropsById('end_applause') });
	const soundEndOK = () => sound_end_ok.play();

	const sound_end_ko = new Howl({ ...getSoundPropsById('fail') });
	const soundEndKO = () => sound_end_ko.play();
	/* SOUNDS - end */

	const is_instructions = props?.is_instructions ?? false;
	if(is_instructions) {
		is_playable = false;
		is_skip_enabled = false;
	}

	const words = useRef([]);
	const stoppedGame = useRef(false);
	const intervalSkip = useRef(null);
	const [ is_loaded, setIsLoaded ] = useState(false);	// to detect if items.current is filled

	const [state, dispatch] = useReducer((state, action) => {
		switch(action.type) {
			case 'NEXT_WORD':
				if(intervalSkip.current) clearInterval(intervalSkip.current);

				const total_questions = words.current.length;
				const solved_questions = words.current.filter((w) => w.success === true).length;
				let ended_game = solved_questions == words.current.length;

				let next_index = state.word_index;
				if(!ended_game) {
					do {
						next_index = ((next_index + 1) % words.current.length)
					} while(words.current[next_index].success === true);

					if(level == 'hard' && total_questions > 1 && (solved_questions + 1) < total_questions && is_skip_enabled) {
						intervalSkip.current = setTimeout(() => {
							dispatch({ type:'DISPLAY_SKIPWORD' });
						}, time_display_skip * 1000);
					}
				}

				return {
					...state,
					word_index:next_index,
					moving_next_question:false,
					display_skipword:false,
					word_with_errors:false,
					points_in_word:points_no_fail,
				};

			case 'ERROR_MOVE':
				words.current[state.word_index].points = points_with_fail;
				soundError();
				return { ...state, word_with_errors:true, points_in_word:points_with_fail };

			case 'DISPLAY_SKIPWORD':
				return { ...state, display_skipword:true };

			case 'HIDE_SKIPWORD':
				return { ...state, display_skipword:false };

			case 'CHANGE':
				return { ...state, [action.k]:action.v };

			case 'SET_FULL_LOADED':
				// to detect if all images (if any) are loaded
				return { ...state, full_loaded:true };

			default:
				return state;
		}
	}, {
		word_index:-1,
		full_loaded:false,
		moving_next_question:false,
		display_skipword:false,
		word_with_errors:false,
		points_in_word:points_no_fail,
	});

	const letter_size = useRef(0);
	const image_bg = useRef({});
	const moving = useRef(0);
	const items = useRef([]);
	const final = useRef([]);
	const longest_length = useRef(max_letters_x_line);
	const longest_line_length = useRef(0);

	const refs = useRef([]);

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	let images_to_load = [];

	useEffect(() => {
		//if(is_playable)return;
		const letters_group = refs.current['letters_group'];
		if(letters_group != undefined){
			letters_group.children.forEach((group) => {
				refs.current[`${group.attrs.id}_letter`].cache(default_cache_props);
			});
			redraw();
		}
	}, [ props ]);

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		gameInit();

		return () => {
			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		}
	}, []);

	useImperativeHandle(ref, () => ({
		stopGame(success_game,cb) {
			if(!success_game) {
				if(!is_test) {
					soundEndKO();
				} else {
					// Delayed 1500 ms because in GameContainer > success there
					// is a delay of 1500 ms to display the final page:
					setTimeout(() => {
						soundEndKO();
					}, 1.5 * 1000);
				}
			}

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			stoppedGame.current = true;
			const cleanup = () => {
				//exitGame();
				if(cb != undefined) {
					setTimeout(() => {
						cb();
					}, 200);
				}
			}
			cleanup();
		},
	}), []);

	const loadImages = (cb) => {
		const img_base_path = imgPath(EP.BUCKET,stage.promotion_id,'stages');

		if(stage.guessword_letter_bg_img != undefined) {
			images_to_load.push({
				id:'letter_bg',
				path:`${img_base_path}${stage.guessword_letter_bg_img}`,
			});
		}
		if(stage.guessword_letter_bg_ok_img != undefined) {
			images_to_load.push({
				id:'letter_bg_ok',
				path:`${img_base_path}${stage.guessword_letter_bg_ok_img}`,
			});
		}

		if(stage.guessword_bg_img != undefined) {
			images_to_load.push({
				id:'main_bg',
				path:`${img_base_path}${stage.guessword_bg_img}`,
			});
		}

		words.current.map((word, w) => {
			if(word.image != undefined && word.image != "") {
				images_to_load.push({
					id:`hint_${w}_bg`,
					path:`${img_base_path}${word.image}`,
				});
			}
		});

		let total_images_pending = images_to_load.length;

		if(total_images_pending == 0) {
			if(cb != undefined) cb();
			dispatch({ type:'SET_FULL_LOADED'});
			return;
		}

		images_to_load.map((image) => {
			let _img = new window.Image();
			_img.crossOrigin = 'Anonymous';
			_img.src = image.path;
			_img.onload = () => {
				handleLoad(_img, image.id);
				total_images_pending--;

				if(total_images_pending == 0) {
					if(cb != undefined) cb();
					dispatch({ type:'SET_FULL_LOADED' });
				}
			};
		});
	};

	const getWordPhrase = (phrase) => {
		let _phrase = phrase?.name_ok ?? phrase.name;
		return _phrase.replace("  ", " ").trim();
	};

	const handleLoad = (image, id) => {
		image_bg.current[id] = {
			fillPatternImage:image,
			image_w:image.width,
			image_h:image.height,
		};
	};

	const findLongestWordInPhrase = (word_id) => {
		if(words.current.length < 1) return null;

		if(is_instructions) return 10;

		let max_num_letters_in_word = 0;
		let longest_length = 1;
		let num_letters_in_word = 0;

		(getWordPhrase(words.current[word_id]).split(" ")).map((word) => {
			num_letters_in_word += word.trim().length + 3;
			longest_length = Math.max(longest_length, word.trim().length);
		});
		num_letters_in_word -= 1;

		if((num_letters_in_word) > max_num_letters_in_word) max_num_letters_in_word = num_letters_in_word;

		// In case the phrase has more words to fit in more than one line, make them smaller:
		const longest_length_phrase = Math.ceil(max_num_letters_in_word / max_letters_x_line);

		return Math.min(longest_length * longest_length_phrase, max_letters_x_line);
	};

	const findLongestLineInPhrase = (lines) => {
		if(lines.length < 1) return null;
		let max_length = 0;
		lines.map((line) => {
			if(line.length > max_length) max_length = line.length;
		});
		return max_length;
	};

	/*
	 We try to normalize the lines of text so there's the more similar lengths among all the text lines.
	 We do this by moving the last word of the longest text line to the next line.
	 */
	const normalizeLines = (lines) => {
		// if we only have 1 line of text, don't do anything:
		if(lines.length < 2) return lines;

		let medium_line_length = 0;		// average line length
		let longest_line = 0;			// number of characters in the longest line
		let longest_line_number = 0;	// index of the longest line
		lines.map((line, l) => {
			if(line.length > longest_line) {
				// we update the longest line:
				longest_line = line.length;
				longest_line_number = l;
			}

			// we calculate the average length:
			medium_line_length = (l == 0) ? line.length : ((medium_line_length + line.length) / (l + 1));
		});

		const words_longest_line = lines[longest_line_number].split(" ");	// get the words in the longest line:
		
		// only one word in the line, so no possible cuts to be done:
		if(words_longest_line.length == 1) return lines;

		const permitted_variance = 4; // letters from the medium line length
		let optimized_lines = [];
		const longest_line_last_word = words_longest_line[words_longest_line.length - 1];

		// the longest line is the last one. We can try to improve this by adding an extra line, but still in TO DO:
		if(longest_line_number == lines.length) return lines;

		// if the resulting line is going to differ so much from the medium length, don't do anything because
		// the problem will persist but in a different line:
		if(longest_line - longest_line_last_word.length < medium_line_length - permitted_variance) return lines;

		lines.map((line, l) => {
			let line_words = line.split(" ");
			if(l == longest_line_number) {
				// we extract the line except the last word:
				optimized_lines.push(line_words.slice(0, line_words.length - 1).join(" "));
			} else if(l == longest_line_number + 1) {
				// we add the moved word to the beginning of the line:
				optimized_lines.push(lines[longest_line_number].split(" ").slice(-1) + " " + line_words.join(" "));
			} else {
				optimized_lines.push(line);
			}
		});

		return optimized_lines;
	};

	useEffect(() => {
		if(state.full_loaded) redraw();
		const _has_more_questions = (words.current.filter((w) => w.success !== true).length > 0) ? true : false;
		if(state.word_index < 0 ||!_has_more_questions) return;
		setIsLoaded(false);
		let _current = getWordPhrase(words.current[state.word_index]);
		let in_pieces = [];

		longest_length.current = findLongestWordInPhrase(state.word_index);

		if(!words.current[state.word_index].skip) {
			while(_current.length > longest_length.current) {
				let substring = _current.substr(0, max_letters_x_line);
				let position_space = substring.lastIndexOf(" ") >= 0 ? substring.lastIndexOf(" ") : false;
				if(position_space) {
					in_pieces.push(_current.trim().substr(0, position_space));
					_current = _current.substr(substring.lastIndexOf(" ")).trim();
				}
				if(_current.length < max_letters_x_line || !position_space) break;
			}
			in_pieces.push(_current.trim());

			const in_pieces_normalized = normalizeLines(in_pieces);
			longest_line_length.current = findLongestLineInPhrase(in_pieces_normalized);

			final.current = in_pieces_normalized;

			let _items = [];
			let index = 0;
			for(let row = 0; row < in_pieces_normalized.length; row++) {
				let previous_word = {};
				for(let col = 0; col < in_pieces_normalized[row].length; col++) {
					const letter = in_pieces_normalized[row].charAt(col);

					if(_items[row] == undefined) _items[row] = [];

					let obj_letter = {}
					//Basic || Cirilic || Chineses ||Japanese
					if(letter == ' ') {
						obj_letter.type = 'space';
						obj_letter.blocked = true;
						if(!previous_word.space && !previous_word.symbol) {
							if(!is_hard_mode) shuffleLettersInWord(_items[row], index);
							index++;
						}
					} else if(checkIfLetterIsValid(letter,true)) {
						obj_letter.id = uuidv4();
						obj_letter.correct = false;
						obj_letter.blocked = false;
						obj_letter.word = index;
						obj_letter.letter = letter;
						obj_letter.col = col;
						obj_letter.column = col;
						obj_letter.row = row;
						obj_letter.type = 'letter';
					} else {
						obj_letter.type = 'symbol';
						obj_letter.correct = true;
						obj_letter.col = col;
						obj_letter.column = col;
						obj_letter.row = row;
						obj_letter.blocked = true;
						obj_letter.letter = letter;
						if(!is_hard_mode) obj_letter.word = index;
					}

					_items[row][col] = obj_letter;
					previous_word = obj_letter;
				}

				if(!is_hard_mode) shuffleLettersInWord(_items[row], index);
				index++;
			}

			if(is_hard_mode) shuffleAllLetters(_items);
			items.current = _items;

			const max_cols = Math.max(..._items.map((e) => e.length));

			calculateLetterSize(max_cols);

			setIsLoaded(true);

			setTimeout(() => {
				(is_instructions) ? animateInstructions() : animateLettersIn();
			}, 50);
		}
	}, [state.word_index]);

	const calculateLetterSize = (max_cols) => {
		const avail_w = stage_avail_w_prop * size.current;
		const min_size_letter = avail_w / Math.max(longest_line_length.current, 8);
		const max_size_letter = 1.3 * min_size_letter;

		letter_size.current = Math.max(min_size_letter, Math.min(max_size_letter, Math.floor(avail_w / max_cols)));
	};

	const shuffleAllLetters = (items) => {
		if(is_instructions) return items;

		let _items = [];
		items.map((row) => {
			row.map((item) => {
				if(item.type == 'letter' || item.type == 'symbol') _items.push(item.letter);
			});
		});

		let words = _derange(_items);
		let _letter_count = 0;

		items.map((row, i) => {
			row.map((item, j) => {
				if(item.type == 'letter' || item.type == 'symbol') {
					items[i][j].letter = words[_letter_count];
					_letter_count++;
				}
			});
		});
	};

	const shuffleLettersInWord = (_items, index) => {
		const items = (_items != undefined) ? _items : [];

		if(is_instructions) return items;

		let words = _derange(items.filter((w) => w.word == index && w.type == 'letter').map(({ letter }) => letter));
		let symbols = items.filter((w) => w.word == index && w.type == 'symbol');
		let locked_cols = symbols.map(({ column }) => column);
		let _letter_count = 0;
		let _symbol_count = 0;
		items.map((w, j) => {
			if(w.word == index) {
				items[j].letter = (locked_cols.indexOf(j) > -1) ? symbols[_symbol_count].letter : words[_letter_count];
				if(locked_cols.indexOf(j) > -1)_symbol_count++;
				else _letter_count++;
			}
		});
	};

	const gameInit = (from_resize) => {
		if(from_resize == undefined) from_resize = false;

		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current = size_received;
				//letter_size.current = size_received / board_size;
				prepareNewGame(() => {
					loadImages(() => {
						dispatch({ type:'NEXT_WORD' });
						props.startTimer();
					});
				});
			});
		}
	};

	const redraw = () => {
		const layer = refs.current['layer'];
		if(layer != undefined) layer.draw();
	};

	const prepareNewGame = (cb) => {
		if(is_instructions) {
			words.current = [{
				description:" ",
				id:"instructions",
				name:"CBA",
				points:"100",
				skip:false,
				type:"guessword_field",
			}];
			if(cb != undefined) cb();
			return;
		}

		let words_init;
	
		if(stage._word_default) {
			words_init = (stage.guessword_words).filter((w) => w.id == stage._word_default);
		} else {
			words_init = stage.guessword_words.map(w=>({...w})) || [];
		}

		const randomly_ordered = stage?.guessword_random_words ?? false;
		const all_words = [...words_init];
		if(all_words.length > 1 && randomly_ordered) all_words.sort(() => Math.random() - 0.5);

		//check words no more longest than max
		for(let i = 0; i < all_words.length; i++) {
			all_words[i].skip = false;
			all_words[i].rtl = false;
			if(detectIfTextIsRTL(all_words[i].name)) {
				// in arabic and hebrew we need to reverse the letters
				//all_words[i].name_ok = all_words[i].name.split("").reverse().join("");
				all_words[i].name_ok = all_words[i].name;
				all_words[i].rtl = true;
			} else {
				all_words[i].name_ok = all_words[i].name;
			}

			let split_words = getWordPhrase(all_words[i]).split(" ");
			for(let j = 0; j < split_words.length; j++) {
				if(split_words[j] == "") continue;

				if(split_words[j].length > longest_length.current || split_words[j].length < min_letters_x_word) {
					all_words[i].skip = true; //Word length supperated
					break;
				}
			}
		}
		const limit_words = stage.guessword_min_words || all_words.length;
		words.current = all_words.slice(0, limit_words);

		if(cb != undefined) cb();
	};

	const handleDragStart = (id) => {
		if((!is_playable && !is_instructions) || moving.current > 0 || refs.current[id] == undefined) return;

		refs.current[id].moveToTop();
		refs.current[id].findOne('.is_bg').to({
			strokeWidth:cell_padding,
			duration:0.2,
		});

		redraw();
	};

	const dragEnd = (id) => {
		if((!is_playable && !is_instructions) || moving.current > 0 || refs.current[id] == undefined) return;

		refs.current[id].findOne('.is_bg').to({
			strokeWidth:0,
			duration:0.2,
		});

		if(!is_instructions) soundSlide();

		redraw();
	};

	const getWordLetters = (word) => {
		return refs.current['letters_group'].children.filter((w) => w.attrs.word == word);
	};

	const swapLetters = (e) => {
		let source_letter = findById(e.currentTarget.attrs.id);
		if(source_letter === false) return false;

		dragEnd(e.currentTarget.attrs.id);

		let valid_intersection = false;
		let element_to_change = false;
		let correct_between_min_and_max = false;
		let letters_to_move = [];
		let direction = 1;

		refs.current['letters_group'].children.forEach((group) => {
			let target_letter = findById(group.attrs.id);
			if(target_letter !== false && (source_letter.word == target_letter.word || is_hard_mode) && source_letter.id != target_letter.id && !target_letter.correct) {
				const source_values = e.currentTarget.getClientRect();
				const target_values = group.getClientRect();
				if(haveIntersection(target_values, source_values)) {
					const area_affected = areaIntersected(target_values, source_values);	//Source vs Target
					const min_area_to_affect_for_change = letter_size.current * letter_size.current * 0.5; //Minim 50% area per fer el canvi
					if(min_area_to_affect_for_change <= area_affected) {
						if(!is_hard_mode) {
							valid_intersection = true;
							element_to_change = group.attrs;

							if(source_letter.column < target_letter.column) direction = -1;

							let letter_start;
							let letter_end;
							if(direction < 0) {
								letter_start = source_letter.column + 1;
								letter_end = target_letter.column;
							} else {
								letter_start = target_letter.column;
								letter_end = source_letter.column - 1;
							}

							const letters_word = getWordLetters(source_letter.word);
							letters_word.map((l) => {
								let ll = {...l};
								const _actual = ll.attrs;
								if(_actual.column >= letter_start && _actual.column <= letter_end){
									if(_actual.correct || _actual.type == 'symbol') {//Symbol es considera correcte
										correct_between_min_and_max = true;
										return;
									}
									letters_to_move.push(l.attrs);
								}
							})
						} else {
							//Hard Mode
							valid_intersection = true;
							element_to_change = group.attrs;
						}
					}
				}
			}
		});

		//tornemlo a lorigen
		if(!valid_intersection) {
			moveAnimate(source_letter);
		}

		if(valid_intersection && element_to_change) {
			if(!correct_between_min_and_max && !is_hard_mode) {
				let movements = [];
				movements.push({ id:source_letter.id, to:element_to_change.column });
				letters_to_move.map((lett) => {
					movements.push({id:lett.id, to:lett.column + direction});
				});

				moveElements(movements, source_letter);
			} else {
				changeElements(e.currentTarget.attrs, element_to_change);
			}
		}
	};

	const getPoints = () => {
		let _points = 0;

		words.current.map((word, i) => {
			if(word.success !== true) return;
			_points += parseInt(word.points);
			//printToConsole(word.success, i, _points);
		});

		return _points;
	};

	const setSuccessFullWords = (word_index, success, cb) => {
		if(is_instructions) return;

		if(success != undefined) {
			words.current[word_index].success = success;
			words.current[word_index].points = words.current[word_index]?.points ?? points_no_fail;

			setTimeout(() => {
				props.onScore(getPoints());
				const solved_questions = words.current.filter((w) => w.success === true).length;
				const ended_game = solved_questions == words.current.length;
				if(ended_game) props.forceStop();
			
				if(cb != undefined) cb(ended_game);
			}, 10);
		}
		//return false;
	};

	const displayScore = () => {
		if(is_instructions) return;

		const has_errors = state?.word_with_errors ?? false;

		refs.current[`completed_perfect`].cache(default_cache_props);
		refs.current[`score`].cache(default_cache_props);

		refs.current[`completed_perfect`].setAttrs({
			opacity:0,
			y:0.5 * size.current,
			scaleX:0,
			scaleY:0,
		});

		refs.current[`score`].setAttrs({
			opacity:0,
			x:0.5 * size.current,
			y:1.5 * size.current,
		});

		redraw();

		if(!has_errors) {
			soundPerfect();

			if(refs.current[`completed_perfect`] != undefined) {
				refs.current[`completed_perfect`].to({
					opacity:1,
					scaleX:2,
					scaleY:2,
					duration:1 * stroke_anim_duration,
					easing:Konva.Easings.BounceEaseOut,
					onFinish:() => {
						setTimeout(() => {
							if(refs.current[`completed_perfect`] != undefined) {
								refs.current[`completed_perfect`].to({
									opacity:0,
									scaleX:0,
									scaleY:0,
									duration:stroke_anim_duration,
									easing:Konva.Easings.EaseOut,
									onFinish:() => {
										if(refs.current[`completed_perfect`] != undefined) {
											refs.current[`completed_perfect`].setAttrs({
												y:1.5 * size.current,
											});

											redraw();
										}
									},
								});
							}
						}, 500);
					},
				});
			}
		};

		setTimeout(() => {
			soundSuccessWord();

			if(refs.current[`score`] != undefined) {
				refs.current[`score`].to({
					opacity:1,
					y:0.5 * size.current,
					duration:0.85 * stroke_anim_duration,
					easing:Konva.Easings.StrongEaseOut,
					onFinish:() => {
						setTimeout(() => {
							if(refs.current[`score`] != undefined) {
								refs.current[`score`].to({
									opacity:0,
									x:size.current,
									y:0,
									duration:1.3 * stroke_anim_duration,
									easing:Konva.Easings.StrongEaseInOut,
									onFinish:() => {
										if(refs.current[`score`] != undefined) {
											refs.current[`score`].setAttrs({
												x:0.5 * size.current,
												y:1.5 * size.current,
												opacity:0,
											});

											redraw();
										}
									},
								});
							}
						}, 200);
					},
				});
			}
		}, ((!has_errors) ? (1.5 * stroke_anim_duration) : 0) * 1000);
	};

	const endSlide = (success) => {
		setSuccessFullWords(state.word_index, success, (ended_game) => {
			setTimeout(() => {
				const _actual_index = state.word_index;
				let _next_index = _actual_index;
				if(!ended_game) {
					// NO hi haura mes paraules pendents si es ended
					do {
						_next_index = ((_next_index + 1) % words.current.length)
					} while (words.current[_next_index].success === true);
				}

				updatePagination();

				if(success) displayScore();

				animateLettersOut(() => {
					// TO DO si succes es false, no faci els saltets. => Marc
					if(!ended_game) {
						dispatch({ type:'NEXT_WORD' });

						// RESUME TIME!
						printToConsole(`Go to slide ${state.word_index + 1}!`);
						printToConsole(`THIS WON'T LOAD THE NEW SLIDE -> VERIFY WHY`);

						dispatch({ type:'CHANGE', moving_next_question:false });
					} else {
						endGame(true);
					}
				});
			}, 1000);
		})
	};

	const updatePagination = () => {
		const _words = words.current;

		const success_words = _words.filter((w) => (w.success));
		if (refs.current && refs.current['pagination_text'] != undefined) {
			refs.current['pagination_text'].setAttrs({ text:`${success_words.length} / ${_words.length}` });
		} 
		redraw();
	};

	const endGame = (success_game) => {
		(success_game) ? soundEndOK() : soundEndKO();
		props.onEnded(success_game);
	};

	const validateWords = (force_status = null) => {
		let all_letters_correct = true;

		if(refs.current['letters_group'] == undefined) return;

		refs.current['letters_group'].children.forEach((group) => {
			let item = findById(group.attrs.id);
			if(item.type != 'letter') return;

			const previous_correct = group.attrs?.correct ?? false;

			const is_correct = (force_status === null) ? ((final.current[item.row].charAt(item.column) == item.letter) ? true : false) : force_status;
			if(all_letters_correct && !is_correct) all_letters_correct = false;

			const has_changed = (!previous_correct && is_correct) ? true : false;

			group.setAttrs({
				correct:is_correct,
				draggable:!is_correct,
			});

			if(has_changed) refs.current[`${group.attrs.id}_letter`].clearCache();

			if(!is_image) {
				group.findOne('.is_bg').to({
					fill:(is_correct) ? background_color_ok : background_color,
					easing:Konva.Easings.EaseInOut,
					duration:stroke_anim_duration * 0.7,
				});
			} else {
				if(letter_image_bg_ok !== false) {
					group.findOne('.is_bg').to({
						opacity:(is_correct) ? 0 : 1,
						easing:Konva.Easings.EaseInOut,
						duration:stroke_anim_duration * 0.7,
					});

					group.findOne('.is_bg_ok').to({
						opacity:(is_correct) ? 1 : 0,
						easing:Konva.Easings.EaseInOut,
						duration:stroke_anim_duration * 0.7,
					});
				} else {
					group.findOne('.is_bg_ok').to({
						opacity:(is_correct) ? 0.3 : 0,
						easing:Konva.Easings.EaseInOut,
						duration:stroke_anim_duration * 0.7,
					});
				}
			}

			group.findOne('.letter_text').to({
				fill:(is_correct) ? text_color_ok : text_color,
				easing:Konva.Easings.EaseInOut,
				duration:stroke_anim_duration * 0.7,
			});

			if(has_changed) {
				setTimeout(() => {
					if(refs.current[`${group.attrs.id}_letter`] != undefined) {
						refs.current[`${group.attrs.id}_letter`].cache(default_cache_props);
					}
				}, (stroke_anim_duration * 0.7 * 1000));
			}
		});

		if(all_letters_correct) endSlide(true);
		redraw();
	};

	const moveElements = (elements, source = null) => {
		let some_target_correct = false
		elements.map((item, i) => {
			let element = findById(item.id);
			element.column = item.to;
			moveAnimate(element, () => {
				if(i == elements.length - 1) {
					validateWords();
					if(!source.correct && !some_target_correct) {
						dispatch({ type:'ERROR_MOVE'});
					}
				} else if(element.correct) {
					some_target_correct = true;
				}
			});
		});
	};

	const changeElements = (source, target) => {
		const _source = { ...source };
		const _target = { ...target };

		source.column = _target.column;
		target.column = _source.column;
		if(is_hard_mode) {
			source.row = _target.row;
			target.row = _source.row;
		}

		moveAnimate(source);
		moveAnimate(target, () => {
			validateWords();
			if(!source.correct && !target.correct) {
				dispatch({ type:'ERROR_MOVE'});
			}
		});
	}

	const findById = (id) => {
		if(refs.current[id] == undefined) return false;
		return refs.current[id].attrs;
	}

	const moveAnimate = (item, cb) => {
		let rect = refs.current[item.id];

		if(rect) {
			moving.current++;
			rect.to({
				x:item.column * letter_size.current,
				y:item.row * letter_size.current,
				easing:Konva.Easings.BounceEaseOut,
				duration:stroke_anim_duration,
				onFinish:() => {
					moving.current--;
					if(moving.current == 0) {} //Deshabilitar blocks
					if(cb != undefined) cb();
					redraw();
				},
			});
		}
	};

	const areaIntersected = (source, target) => {
		const _x1 = source.x;
		const _x2 = _x1 + letter_size.current;
		const _y1 = source.y;
		const _y2 = _y1 + letter_size.current;

		const _x3 = target.x;
		const _x4 = _x3 + letter_size.current;
		const _y3 = target.y;
		const _y4 = _y3 + letter_size.current;

		const dx = Math.min(_x2, _x4) - Math.max(_x1, _x3);
		const dy = Math.min(_y2, _y4) - Math.max(_y1, _y3);

		if(dx >= 0 && dy >= 0) return dx * dy;
	};

	const haveIntersection = (r1, r2) => {
		return !(
			r2.x > r1.x + r1.width ||
			r2.x + r2.width < r1.x ||
			r2.y > r1.y + r1.height ||
			r2.y + r2.height < r1.y
		);
	};

	const verifyIfLettersLoadedPromise = (cb) => {
		if(state.word_index > 0) {
			cb();
		}
		const promise = new Promise((solve, reject) => {
			if(refs.current['letters_group'] != undefined) {
				solve();
			}
		})

		promise.then(cb, () => {});
	};

	const animateLettersIn = (cb) => {
		if(!is_playable && !is_instructions) return;

		const in_delay = 80;	// ms

		const letter_anim_duration = stroke_anim_duration;

		verifyIfLettersLoadedPromise(() => {
			const letters = refs.current['letters_group'].children;
	
			if(refs.current['word_hint'] != undefined) {
				refs.current['word_hint'].setAttrs({ opacity:0 });
				refs.current['word_hint'].to({
					opacity:1,
					duration:letter_anim_duration + (letters.length - 1) * (in_delay / 1000),
				});
			}

			letters.forEach((group, g) => {
				refs.current[`${group.attrs.id}_letter`].cache(default_cache_props);

				refs.current[group.attrs.id].setAttrs({
					x:group.attrs.x_ini,
					y:group.attrs.y_ini,
					rotation:60,
					scaleX:0.2,
					scaleY:0.2,
					opacity:0,
				});
	
				setTimeout(() => {
					if(refs.current[group.attrs.id] != undefined) {
						refs.current[group.attrs.id].to({
							x:(appear_method == 'left') ? group.attrs.x_end : group.attrs.x_ini,
							y:(appear_method == 'bottom') ? group.attrs.y_end : group.attrs.y_ini,
							rotation:0,
							scaleX:1,
							scaleY:1,
							opacity:1,
							easing:Konva.Easings.EaseInOut,
							duration:letter_anim_duration,
							onFinish:() => {
								if(g == letters.length - 1) {
									if(cb != undefined) cb();
								}
							}
						});
					}
				}, (g * in_delay));
			});
		});
	};

	const animateLettersOut = (cb) => {
		if((!is_playable && !is_instructions) || refs.current['letters_group'] == undefined) return;

		const jump_delay = 70;	// ms
		const out_delay = 80;	// ms

		const letter_anim_duration = stroke_anim_duration;
		const letters = refs.current['letters_group'].children;

		if(!is_instructions && refs.current['word_hint'] != undefined) {
			refs.current['word_hint'].setAttrs({ opacity:1 });
			refs.current['word_hint'].to({
				opacity:0,
				duration:letter_anim_duration + (letters.length - 1) * (out_delay / 1000),
			});
		}

		letters.forEach((group, f) => {
			let y_start = group.attrs.y;

			setTimeout(() => {
				if(refs.current[group.attrs.id] != undefined) {
					refs.current[group.attrs.id].to({
						y:y_start - 10,
						easing:Konva.Easings.EaseInOut,
						duration:0.25 * letter_anim_duration,
						onFinish:() => {
							if(refs.current[group.attrs.id] != undefined) {
								refs.current[group.attrs.id].to({
									y:y_start,
									easing:Konva.Easings.EaseInOut,
									duration:0.25 * letter_anim_duration,
									onFinish:() => {
										if(f == letters.length - 1) {
											setTimeout(() => {
												letters.slice().reverse().forEach((group, g) => {
													setTimeout(() => {
														if(refs.current[group.attrs.id] != undefined) {
															refs.current[group.attrs.id].to({
																x:(!is_instructions) ? ((appear_method == 'left') ? group.attrs.x_ini : group.attrs.x_end) : (size.current + group.attrs.x_ini),
																y:(appear_method == 'bottom') ? group.attrs.y_ini : group.attrs.y_end,
																rotation:60,
																scaleX:0.5,
																scaleY:0.5,
																easing:Konva.Easings.EaseInOut,
																duration:letter_anim_duration,
																onFinish:() => {
																	if(g == letters.length - 1) {
																		if(cb != undefined) cb();
																	}
																}
															});
														}
													}, (g * out_delay));
												});
											}, 300);
										}
									},
								});
							}
						}
					});
				}
			}, (f * jump_delay));
		});
	};

	// Letter item configuration -- ini
	let is_circle = (letter_layout == 'circle') ? true : false;
	let is_square = (letter_layout == 'square') ? true : false;
	let is_image = false;
	if(letter_layout == 'image'){
		if(image_bg.current['letter_bg'] != undefined && image_bg.current['letter_bg'] != ''){
			is_image = true;
		} else {
			is_circle = true;
		}
	}
	const letter_image_bg = (is_image && image_bg.current['letter_bg'] != undefined) ? image_bg.current['letter_bg'] : false;
	const letter_image_bg_ok = (is_image && image_bg.current['letter_bg_ok'] != undefined) ? image_bg.current['letter_bg_ok'] : false;
	const letter_img_size = (letter_image_bg !== false) ? letter_size.current / (Math.max(letter_image_bg.image_w, letter_image_bg.image_h)) : 0;
	const letter_img_ok_size = (letter_image_bg_ok !== false) ? letter_size.current / (Math.max(letter_image_bg_ok.image_w, letter_image_bg_ok.image_h)) : 0;
	// Letter item configuration -- end

	const instructions_initials = useRef(false);

	const animateInstructions = () => {
		if(!is_instructions || refs.current['instructions'] == undefined) return null;

		if(instructions_initials.current === false) {
			instructions_initials.current = [
				{ ...refs.current['letters_group'].children[0].attrs },
				{ ...refs.current['letters_group'].children[1].attrs },
				{ ...refs.current['letters_group'].children[2].attrs },
			];
		}

		refs.current['instructions'].setAttrs({
			x:100,
			y:letter_size.current / 2 + 250,
			opacity:0,
		});

		redraw();

		animateLettersIn(() => {
			if(refs.current['instructions'] == undefined) return;

			refs.current['instructions'].setAttrs({
				opacity:1,
			});
			redraw();

			refs.current['instructions'].to({
				x:letter_size.current / 2 - 8,
				y:letter_size.current / 2 + 8,
				duration:stroke_anim_duration,
				easing:Konva.Easings.EaseInOut,
				onFinish:() => {
					handleDragStart(instructions_initials.current[0].id);

					if(refs.current[instructions_initials.current[0].id] != undefined) {
						refs.current[instructions_initials.current[0].id].to({
							x:instructions_initials.current[2].x,
							duration:1.5 * stroke_anim_duration,
							easing:Konva.Easings.EaseInOut,
							onFinish:() => {
								dragEnd(instructions_initials.current[0].id);
								validateWords(true);

								if(refs.current[instructions_initials.current[2].id] != undefined) {
									refs.current[instructions_initials.current[2].id].to({
										x:instructions_initials.current[0].x,
										duration:stroke_anim_duration,
										easing:Konva.Easings.BounceEaseOut,
										onFinish:() => {
											animateLettersOut(() => {
												validateWords(false);

												setTimeout(() => {
													animateInstructions();
												}, (stroke_anim_duration * 1000));
											});
										},
									});
								}

								setTimeout(() => {
									if(refs.current['instructions'] != undefined) {
										refs.current['instructions'].to({
											y:250,
											duration:0.5,
											easing:Konva.Easings.EaseInOut,
											onFinish:() => {
												if(refs.current['instructions'] != undefined){
													refs.current['instructions'].setAttrs({
														opacity:0,
													});
													redraw();
												}
											},
										});
									}
								}, 200);
							},
						});
					}
				},
			});
		});
	};

	const skipWord = () => {
		dispatch({ type:'HIDE_SKIPWORD' });
		endSlide(false);
	};

	let word_skipped = (words.current).find((w) => {
		return w.skip != undefined && w.skip;
	});

	if(word_skipped) {
		return (
			<div class={"is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big"}>
				<FormattedMessage
					id="Some word / phrase has a word with more than {max} characters."
					values={{ max:max_letters_x_line}}
				/>
			</div>
		)
	}

	if(!state.full_loaded || state.moving_next_question) {
		return (
			<div class={main_classes.join(" ")}>
				<Spinner>{(state.moving_next_question) ? "Next question" : "Loading game"}</Spinner>
			</div>
		)
	}

	const actual_word = words.current[state.word_index];
	if(!actual_word) return;

	const _rows = items.current.length;

	if(!is_loaded || _rows == 0) return null;

	// Background configuration -- ini
	const main_bg = image_bg.current?.main_bg ?? false;
	const main_bg_size = (main_bg !== false) ? size.current / (Math.max(main_bg.image_w, main_bg.image_h)) : 0;
	// Background configuration -- end

	// Word hint image configuration -- ini
	const hint_bg = image_bg.current[`hint_${state.word_index}_bg`] ?? false;
	const hint_bg_size = (hint_bg !== false) ? (0.5 * size.current) / hint_bg.image_h : 0;
	// Word hint image configuration -- end
	
	const page_size = 10;
	const page_size_padding = page_size;
	const page_size_gap = page_size;
	const page_width = (size.current - 2 * page_size_padding - page_size_gap * (words.current.length - 1)) / words.current.length;

	const game_area_w = stage_avail_w_prop * size.current;
	let game_area_h = 0.5 * size.current;
	if(words.current.length > 1) game_area_h -= 1.2 * page_size_padding;

	let letters_bounding_h = _rows * letter_size.current;
	let letters_bounding_w = 0;

	items.current.map((row) => {
		letters_bounding_w = Math.max(letters_bounding_w, row.length * letter_size.current);
	});

	let hint_text = actual_word?.description ?? "";
	if(hint_text == "") hint_text = i18n("Sort the letters below to get the enigma");

	const font_family = stage?.guessword_font_family ?? 'generic';
	const layout_props = getLayout(promotion);
	const main_font = layout_props.text.main_font;
	const secondary_font = layout_props.text.secondary_font;
	let typography;
	switch(font_family) {
		case 'main':
			typography = main_font;
			break;

		case 'secondary':
			typography = secondary_font;
			break;

		case 'generic':
		default:
			typography = 'Arial';
			break;
	}

	const is_rtl_word = actual_word?.rtl ?? false;

	return (
		<Stage
			ref={(node) => { refs.current[`main_stage`] = node; }}
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
		>
			<Layer ref={(node) => { refs.current[`layer`] = node; }}>
				{(main_bg !== false) ? (
					<Rect
						width={size.current}
						height={size.current}
						fillPatternImage={main_bg.fillPatternImage}
						fillPatternRepeat='no-repeat'
						fillPatternScaleX={main_bg_size}
						fillPatternScaleY={main_bg_size}
					/>
				) : (
					<>
						<Rect
							width={size.current}
							height={size.current}
							fill={main_color}
						/>

						<Rect
							width={size.current}
							height={0.5 * size.current}
							fill={(is_main_color_bright) ? 'black' : 'white'}
							opacity={0.2}
						/>
					</>
				)}

				<Group
					ref={(node) => { refs.current[`word_hint`] = node; }}
					height={0.5 * size.current}
					opacity={(is_playable) ? 0 : 1}
				>
					{(hint_bg !== false) ? (
						<Rect
							width={hint_bg.image_w * hint_bg_size}
							height={0.5 * size.current}
							fillPatternImage={hint_bg.fillPatternImage}
							fillPatternRepeat='no-repeat'
							fillPatternScaleX={hint_bg_size}
							fillPatternScaleY={hint_bg_size}
						/>
					) : (
						<HightlightedText
							canvas_size={size.current}
							avail_w={stage_avail_w_prop}
							stroke_width={1.5 * cell_padding}
							text_color={hint_text_color}
							typography={typography}
						>{hint_text}</HightlightedText>
					)}
				</Group>

				<Group
					x={(is_rtl_word) ? ((stage_avail_w_prop * size.current) / 2) : (((2 - stage_avail_w_prop) * size.current) / 2)}
					y={0.5 * size.current}
					offsetX={size.current / 2}
					scaleX={(is_rtl_word) ? -1 : 1}
				>
					<Group
						ref={(node) => { refs.current['letters_group_helpers'] = node; }}
						width={letters_bounding_w}
						height={letters_bounding_h}
						x={(game_area_w - letters_bounding_w + letter_size.current) / 2}
						y={(game_area_h - letters_bounding_h + letter_size.current) / 2}
						opacity={0.1}
					>
						{items.current.map((row, r) => {
							return (
								row.map((element, x) => {
									const init_x = letter_size.current * element.col;
									const init_y = letter_size.current * element.row;

									const blocked = element?.blocked ?? false;
									const letter = element?.letter ?? false;
									const item_type = element?.type ?? 'letter';

									let default_letter_props = {
										width:letter_size.current,
										height:letter_size.current,
									};

									if(element.letter == undefined) return null;

									return (
										<Group
											id={`__${element.id}`}
											key={`bg_position_${state.actual_word}_${element.id}`}
											{...default_letter_props}
											x={init_x}
											y={init_y}
											offsetX={letter_size.current / 2}
											offsetY={letter_size.current / 2}
											row={element.row}
											column={element?.column ?? x}
											word={element.word}
											letter={letter}
											type={item_type}
										>
											<Group
												x={letter_size.current / 2}
												scaleX={(is_rtl_word) ? -1 : 1}
												offsetX={letter_size.current / 2}
											>
												{(!blocked && is_circle) && (
													<Circle
														radius={letter_size.current * 0.5 - cell_padding}
														fill={(is_main_color_bright) ? 'black' : 'white'}
														x={letter_size.current / 2}
														y={letter_size.current / 2}
													/>
												)}

												{(!blocked && (is_square || is_image)) && (
													<Rect
														x={2}
														y={2}
														width={letter_size.current - 2 * cell_padding}
														height={letter_size.current - 2 * cell_padding}
														fill={(is_main_color_bright) ? 'black' : 'white'}
													/>
												)}
											</Group>
										</Group>
									)
								})
							)
						})}
					</Group>

					<Group
						ref={(node) => { refs.current['letters_group'] = node; }}
						width={letters_bounding_w}
						height={letters_bounding_h}
						x={(game_area_w - letters_bounding_w + letter_size.current) / 2}
						y={(game_area_h - letters_bounding_h + letter_size.current) / 2}
					>
						{items.current.map((row, r) => {
							return (
								row.map((element, x) => {
									const init_x = letter_size.current * element.col;
									const init_y = letter_size.current * element.row;

									const blocked = element?.blocked ?? false;
									const correct = element?.correct ?? false;
									const letter = element?.letter ?? false;
									const item_type = element?.type ?? 'letter';

									const locked = (correct || blocked) ? true : false;

									let default_letter_props = {
										width:letter_size.current,
										height:letter_size.current,
									};

									if(element.letter == undefined) return null;

									return (
										<Group
											ref={(node) => { refs.current[element.id] = node; }}
											key={`${state.actual_word}_${element.id}`}
											id={element.id}
											{...default_letter_props}
											x={init_x + ((appear_method == 'left' && is_playable) ? (game_area_w + 50) : 0)}
											y={init_y + ((appear_method == 'bottom' && is_playable) ? (game_area_h + 50) : 0)}
											x_ini={init_x + ((appear_method == 'left' && is_playable) ? (game_area_w + 50) : 0)}
											y_ini={init_y + ((appear_method == 'bottom' && is_playable) ? (game_area_h + 50) : 0)}
											x_end={init_x}
											y_end={init_y}
											rotation={(is_playable) ? 60 : 0}
											scaleX={(is_playable) ? 0.5 : 1}
											scaleY={(is_playable) ? 0.5 : 1}
											draggable={(is_playable && !locked) ? true : false}
											onDragEnd={swapLetters}
											onDragStart={() => handleDragStart(element.id)}
											offsetX={letter_size.current / 2}
											offsetY={letter_size.current / 2}
											hitStrokeWidth={5}
											row={element.row}
											column={element?.column ?? x}
											word={element.word}
											letter={letter}
											type={item_type}
										>
											<Group
												ref={(node) => { refs.current[`${element.id}_letter`] = node; }}
												x={letter_size.current / 2}
												scaleX={(is_rtl_word) ? -1 : 1}
												offsetX={letter_size.current / 2}
											>
												{(!blocked && is_image) && (
													<>
														<Rect
															{...default_letter_props}
															fillPatternImage={letter_image_bg.fillPatternImage}
															fillPatternRepeat='no-repeat'
															fillPatternScaleX={letter_img_size}
															fillPatternScaleY={letter_img_size}
															strokeWidth={0}
															stroke='transparent'
															name='is_bg'
														/>

														{(letter_image_bg_ok !== false) ? (
															<Rect
																{...default_letter_props}
																fillPatternImage={letter_image_bg_ok.fillPatternImage}
																fillPatternRepeat='no-repeat'
																fillPatternScaleX={letter_img_ok_size}
																fillPatternScaleY={letter_img_ok_size}
																name='is_bg_ok'
																opacity={0}
															/>
														) : (
															<Rect
																x={2}
																y={2}
																width={letter_size.current - 2 * cell_padding}
																height={letter_size.current - 2 * cell_padding}
																fill={background_color_ok}
																name='is_bg_ok'
																opacity={0}
															/>
														)}
													</>
												)}

												{(!blocked && is_circle) && (
													<Circle
														radius={letter_size.current * 0.5 - cell_padding}
														fill={background_color}
														x={letter_size.current / 2}
														y={letter_size.current / 2}
														strokeWidth={0}
														stroke={(is_bg_color_bright) ? 'black' : 'white'}
														name='is_bg'
													/>
												)}

												{(!blocked && is_square) && (
													<Rect
														x={2}
														y={2}
														width={letter_size.current - 2 * cell_padding}
														height={letter_size.current - 2 * cell_padding}
														fill={background_color}
														strokeWidth={0}
														stroke={(is_bg_color_bright) ? 'black' : 'white'}
														name='is_bg'
													/>
												)}

												{(item_type != 'space') && (
													<Text
														{...default_letter_props}
														name='letter_text'
														fontSize={letter_size.current * 0.35}
														fontFamily={typography}
														fill={text_color}
														wrap='char'
														fontStyle='bold'
														align='center'
														verticalAlign='middle'
														text={letter}
													/>
												)}

												<Rect
													{...default_letter_props}
													fill='purple'
													opacity={0}
												/>
											</Group>

											{(is_instructions && r == 0 && x == 0) && (
												<Group
													ref={(node) => { refs.current['instructions'] = node; }}
													x={100}
													y={250}
												>
													<InstructionsHand />
												</Group>
											)}
										</Group>
									)
								})
							)
						})}
					</Group>
				</Group>

				{(words.current.length > 1 && !is_instructions) && (
					<Group
						ref={(node) => { refs.current['pagination'] = node; }}
						y={page_size_padding + 5}
						x={0.425 * size.current}
					>
						<Rect
							fill={background_color}
							width={0.15 * size.current}
							height={20}
							cornerRadius={10}
						/>

						<Text
							ref={(node) => { refs.current['pagination_text'] = node; }}
							text={`0 / ${words.current.length}`}
							width={0.15 * size.current}
							fontSize={15}
							align='center'
							y={3}
							fill={text_color}
						/>
					</Group>
				)}

				{(is_skip_enabled && state.display_skipword) && (
					<SkipButton
						color_ok={background_color_ok}
						onClick={() => skipWord()}
						v_anchor='top'
						h_anchor='right'
						container_size={size.current}
						typography={typography}
						intl={props.intl}
					/>
				)}

				<Group>
					<HightlightedText
						reference={(node) => { refs.current[`completed_perfect`] = node; }}
						canvas_size={size.current}
						y={1.5 * size.current}
						avail_w={stage_avail_w_prop}
						stroke_width={3 * cell_padding}
						text_color={hint_text_color}
						typography={typography}
					>{i18n("Perfect!", 'uppercase')}</HightlightedText>

					<HightlightedText
						reference={(node) => { refs.current[`score`] = node; }}
						canvas_size={size.current}
						y={1.5 * size.current}
						avail_w={stage_avail_w_prop}
						stroke_width={3 * cell_padding}
						stroke_opacity={0.8}
						text_color={hint_text_color}
						font_size={40}
						typography={typography}
					>{props.intl.formatMessage({ id:"+ {score} points" }, { score:state.points_in_word })}</HightlightedText>
				</Group>
			</Layer>

			<Layer>
				<Group ref={(node) => { refs.current[`layer_text`] = node; }} />

				<Group ref={(node) => { refs.current[`layer_blocker`] = node; }} />
			</Layer>
		</Stage>
	)
});

export default GuessWordCanvas;